/**
 * Shadows (P0-T07, SPEC-01 §6): cascaded shadow generator on the sun, PCF low,
 * bias 0.004 / normal bias 0.02, stabilised cascades lambda 0.85, blend 8%.
 * Includes the per-cascade frustum culling hook (Babylon's CSM otherwise renders
 * every caster into every cascade) and the `thinCount` helper (D-16).
 */
import type { DirectionalLight, Scene } from 'babylonjs';

export interface ShadowHandle {
  /** R-03 instrumentation: last per-cascade draw counts vs total casters. */
  stats(): { total: number; perCascade: number[]; culled: boolean[]; err: string };
  /** Add a mesh to every cascade's cast list. */
  addCaster(mesh: unknown): void;
  /** Adaptive step 3: refresh shadow map every 2nd frame. */
  setHalfRate(half: boolean): void;
  /** Adaptive step 6: shadow distance × 0.7 (floor 140 m). */
  shrinkDistance(factor: number): void;
  dispose(): void;
}

interface CSM {
  lambda: number;
  blendState: number;
  shadowMaxZ: number;
  stabilization: boolean;
  numCascades: number;
  addShadowCaster(mesh: unknown, includeDescendants?: boolean): void;
  getShadowMap(): {
    bias: number;
    normalBias: number;
    depthClamp?: number;
    usePoissonSampling: boolean;
    useExponentialShadowMap: boolean;
    useBlurExponentialShadowMap: boolean;
    useCloseFilter?: boolean;
    refreshRate: number;
    getCustomRenderList?: (layer: number, list: unknown[], n: number) => unknown[];
  } | null;
  getCascadeTransformMatrix(layer: number): unknown;
  dispose(): void;
}

interface BabylonSceneNs {
  CascadedShadowGenerator: new (mapSize: number, light: DirectionalLight) => CSM;
  Frustum: {
    GetPlanes(matrix: unknown): unknown[];
    GetPlanesToRef(matrix: unknown, planes: unknown[]): void;
  };
  Plane: new (a: number, b: number, c: number, d: number) => unknown;
  Matrix: { Identity(): unknown };
  ShadowGenerator: { ALWAYS_REFRESH: number; REFRESH_ON_DEMAND: number };
}

const BLEND_STATE_POSITIVE = 1; // ShadowGenerator.BLUR_HIGH_QUALITY-ish constant for CSM blend mode

export function createShadows(
  babylon: typeof import('babylonjs'),
  scene: Scene,
  sun: DirectionalLight,
  tier: 'low' | 'mid' | 'high',
): ShadowHandle | null {
  const params = { low: 0, mid: 1, high: 2 }[tier];
  if (params === 0) return null; // Low: no shadows (tiers.json shadows=0)

  const ns = babylon as unknown as BabylonSceneNs;
  const mapSize = tier === 'high' ? 2048 : 1024;
  const csm = new ns.CascadedShadowGenerator(mapSize, sun);
  csm.lambda = 0.85;
  csm.blendState = BLEND_STATE_POSITIVE; // 8% blend between cascades
  csm.stabilization = true;
  csm.shadowMaxZ = 250;
  csm.numCascades = tier === 'high' ? 4 : 3;

  const smap = csm.getShadowMap();
  if (smap) {
    smap.bias = 0.004;
    smap.normalBias = 0.02;
    if ('depthClamp' in smap) smap.depthClamp = 10;
    smap.useExponentialShadowMap = false;
    smap.useBlurExponentialShadowMap = false;
    smap.usePoissonSampling = true; // PCF low quality

    // per-cascade frustum culling (SPEC-01 §6 snippet).
    // GetPlanesToRef writes into EXISTING Plane objects — seed the array like
    // the prototype did (GetPlanes(Matrix.Identity())), else it throws.
    const planes: unknown[] = ns.Frustum.GetPlanes(ns.Matrix.Identity());
    const open = new ns.Plane(0, 0, 0, 1); // always passes
    const test: unknown[] = [open, open, null, null, null, null];
    // R-03 instrumentation: how many casters each cascade actually drew
    const drawn: number[] = new Array(8).fill(0);
    const listLen: number[] = new Array(8).fill(0);
    const culledFlag: boolean[] = new Array(8).fill(false);
    (csm as unknown as { _r03: { drawn: number[]; listLen: number[]; culled: boolean[] } })._r03 = {
      drawn,
      listLen,
      culled: culledFlag,
    };
    smap.getCustomRenderList = (layer: number, list: unknown[], n: number): unknown[] => {
      const out: unknown[] = [];
      listLen[layer % 8] = n;
      let culled = true;
      let mtx: unknown = null;
      try {
        mtx = csm.getCascadeTransformMatrix(layer);
        // Babylon 9: GetPlanesToRef(transform, planeArrayRef) — the ref array
        // holds Plane objects; scene form was removed (prototype used 2-arg).
        ns.Frustum.GetPlanesToRef(mtx, planes);
        if (!planes[2] || !planes[3] || !planes[4] || !planes[5]) culled = false;
      } catch (err) {
        culled = false; // cascade not computed yet (first frames): draw everything
        (csm as unknown as { _r03err?: unknown })._r03err = err;
      }
      if (!culled || !mtx) {
        // pre-frustum frames draw the whole list; count honestly for R-03 but
        // mark the sample so probes can skip it (counts are last-write)
        drawn[layer % 8] = n;
        return list.slice(0, n);
      }
      culledFlag[layer % 8] = true;
      test[2] = planes[2];
      test[3] = planes[3];
      test[4] = planes[4];
      test[5] = planes[5];
      for (let i = 0; i < n; i++) {
        const m = list[i] as {
          isEnabled?(): boolean;
          isVisible?: boolean;
          alwaysSelectAsActiveMesh?: boolean;
          hasThinInstances?: boolean;
          getBoundingInfo?(): { isInFrustum(planes: unknown[]): boolean };
        } | null;
        if (!m || m.isEnabled?.() === false || m.isVisible === false) continue;
        if (
          m.alwaysSelectAsActiveMesh ||
          m.hasThinInstances ||
          (m.getBoundingInfo?.().isInFrustum(test) ?? true)
        ) {
          out.push(m);
        }
      }
      drawn[layer % 8] = out.length;
      return out;
    };
  }

  void scene;
  return {
    stats(): { total: number; perCascade: number[]; culled: boolean[]; err: string } {
      const r = (
        csm as unknown as {
          _r03?: { drawn: number[]; listLen: number[]; culled: boolean[] };
        }
      )._r03;
      const nc = Math.min(csm.numCascades, 8);
      return {
        total: r ? (r.listLen[0] ?? 0) : 0,
        perCascade: r ? r.drawn.slice(0, nc) : [],
        culled: r ? r.culled.slice(0, nc) : [],
        err: String((csm as unknown as { _r03err?: { message?: string } })._r03err?.message ?? 'none'),
      };
    },
    addCaster(mesh: unknown): void {
      try {
        csm.addShadowCaster(mesh, false);
      } catch {
        /* mesh disposed mid-load */
      }
    },
    setHalfRate(half: boolean): void {
      const sm = csm.getShadowMap();
      if (sm) sm.refreshRate = half ? 2 : 1;
    },
    shrinkDistance(factor: number): void {
      csm.shadowMaxZ = Math.max(140, csm.shadowMaxZ * factor);
    },
    dispose(): void {
      csm.dispose();
    },
  };
}

/** Add a mesh to every cascade's cast list (no-op on a null handle). */
export function addCaster(shadow: ShadowHandle | null, mesh: unknown): void {
  shadow?.addCaster(mesh);
}

/**
 * Never set thinInstanceCount to 0 (decision D-16, SPEC-01): keep 1 instance parked
 * offscreen and disable the mesh instead. Returns true when visible.
 */
export function thinCount(
  mesh: { thinInstanceCount: number; setEnabled(on: boolean): void },
  n: number,
): boolean {
  if (n > 0) {
    mesh.thinInstanceCount = n;
    mesh.setEnabled(true);
    return true;
  }
  mesh.thinInstanceCount = 1;
  mesh.setEnabled(false);
  return false;
}
